import {defaultApi} from '../axios'
import type {Maybe} from '../types'
import type {Page} from '../types/pagination'
import AbortControllerService from './abort/AbortControllerService'

export type Frequency = 'NEVER' | 'DAILY' | 'WEEKLY' | 'MONTHLY'

export interface UserSettings {
  language: string | null
  locale: string | null
  showCurrencySymbol: boolean
  rateReport: Frequency
  pickupReport: Frequency
  performanceReport: Frequency
  defaultCompanyId: number | null
}

export interface UserProperties {
  id: number | null
  email: string | null
  username: string | null
  firstname: string | null
  lastname: string | null
  termsAccepted: boolean | null
  settings: UserSettings
}

export interface UserFetchPaginatedOpts {
  page: number
  size: number
  sort?: string
  ascending?: boolean
  ids?: Maybe<number[]>
  username?: string
  firstname?: string
  lastname?: string
  email?: string
  idNotIn?: Maybe<number[]>
}

export interface UserCreateRequest {
  id: number
  firstname: string
  lastname: string
  email: string
  language: string
}

export interface UserUpdateRequest {
  firstname?: string
  lastname?: string
}

export class UsersService extends AbortControllerService {
  fetchUser(userId: number): Promise<UserProperties> {
    return defaultApi.get(`/api/user/users/${userId}`, this.buildConfig()).then((response) => {
      return response.data
    })
  }

  fetchPaginated({
    page,
    size,
    sort,
    ascending,
    ids,
    username,
    firstname,
    lastname,
    email,
    idNotIn,
  }: UserFetchPaginatedOpts): Promise<Page<UserProperties>> {
    const joinedIds = ids && ids.join(',')
    const joinedIdsNotIn = idNotIn && idNotIn.join(',')
    const sortWithDirection = sort ? `${sort},${ascending ? 'asc' : 'desc'}` : undefined
    const params = {
      page,
      size,
      sort: sortWithDirection,
      ids: joinedIds,
      username,
      firstname,
      lastname,
      email,
      idNotIn: joinedIdsNotIn,
    }
    return defaultApi.get<Page<UserProperties>>('/api/user/users', this.buildConfig(params)).then((response) => response.data)
  }

  updateUserSettings(userId: number, settings: UserSettings): Promise<UserSettings> {
    return defaultApi.post(`/api/user/users/${userId}/settings`, settings).then((response) => response.data)
  }

  updateUser(id: number, request: UserUpdateRequest) {
    return defaultApi.put(`/api/user/users/${id}`, request)
  }
}

export default new UsersService()
